import type { PoolShippingService, ShippingServiceCodeName } from '../../../modules/api';
import { PromoPrice } from '../../../modules/shippingPromo';

type Props = {
  services: PoolShippingService[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  /** Backend-quoted price per service id; empty until an address exists. */
  optionPrices: Record<number, number>;
  /** Regular price per service id while a promotion lowers them — struck
   *  through before the option's price where it is higher. */
  originalOptionPrices?: Record<number, number>;
  loading: boolean;
  /** The protection tier the backend requires for this card, if any. */
  requiredCodeName: ShippingServiceCodeName | null;
};

/**
 * Delivery & shipping options as 64px rows (partner Figma 2026-10-08): the
 * backend's option name and its quoted price; the picked one gets a white
 * outline. The option the card requires is marked, never preselected over the
 * player's own pick.
 */
export function ShippingOptionList({
  services,
  selectedId,
  onSelect,
  optionPrices,
  originalOptionPrices = {},
  loading,
  requiredCodeName,
}: Props) {
  return (
    <ul className="m-0 flex list-none flex-col gap-2 p-0">
      {services.map((service) => {
        const isSelected = service.id === selectedId;
        const price = optionPrices[service.id];
        const isRequired = requiredCodeName != null && service.codeName === requiredCodeName;
        return (
          <li key={service.id}>
            <button
              type="button"
              aria-pressed={isSelected}
              onClick={() => onSelect(service.id)}
              className={
                'flex min-h-16 w-full items-center justify-between gap-4 rounded-[14px] border border-solid px-4 py-3 text-left ' +
                'bg-[var(--co-ShipCard-rowFill,#1A1B1F)] ' +
                (isSelected
                  ? 'border-[var(--co-ShipCard-selectedBorder,#FFFFFF)]'
                  : 'border-[var(--co-ShipCard-rowBorder,rgba(255,255,255,0.01))]')
              }
            >
              <span className="flex min-w-0 flex-col">
                <span className="text-[14px] font-medium leading-[20px] tracking-[-0.15px] text-[var(--co-ShipCard-titleColor,#FFFFFF)]">
                  {service.name}
                </span>
                {isRequired && (
                  <span className="text-[12px] leading-[16px] text-[var(--co-ShipCard-mutedText,#6D707C)]">Required for this card</span>
                )}
              </span>
              <span className="shrink-0 whitespace-nowrap text-right text-[14px] font-semibold leading-[20px] tracking-[-0.15px] tabular-nums text-[var(--co-ShipCard-titleColor,#FFFFFF)]">
                {loading || price == null ? (
                  '—'
                ) : (
                  <PromoPrice amount={price} original={originalOptionPrices[service.id]} />
                )}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
